import { Injectable } from "@angular/core";
import { MobileWallet } from "src/app/kiosk/home/home.component";
import { AccessModuleResponse, BTCProduct, BTCProductPlan, Country, GamingHousePlayerSchema, PrepaidPlan } from '../model/api';
import { Carrier, SERVICE_FEES, ZAdmin } from '../../config/constant';
import { EMVResponse } from "./components/EMVModule.service";
import { Cassette } from "./components/billdispenser.service";
import { z } from "zod";
import { CUSTOMER_INFO, ESTIMATE_FEE, FEE_INQUIRY, NewReceiver, PURPOSE, RECEIVER, SOURCE, TRANSACTION_INQUIRY } from "./western-union.service";
import { Step, StepKey } from "./ui.service";
import { WUSend } from "./components/ReceiptPrinterModule.service";
import { NewAccount } from "src/app/kiosk/bank-deposit/receiver-info/receiver-info.component";
import { Bank } from "./bank-deposit.service";
import { ExtendedGamingHouse } from "./gaming.service";

export type ConfirmItem = { label: string, value: string };

export interface BaseSchema {
  user: AccessModuleResponse,
  sm: {
    sender: {
      id: string,
      name: string,
      mobile: string,
      email: string,
      type: 'old_user' | 'new_user'
    },
    receiver: {
      id: string,
      name: string,
      mobile: string,
      email: string,
      type: 'old_user' | 'new_user'
    }
    fees: {
      fee: number,
      vat: number,
    },
    denomination: number,
    type: 'DIRECT' | 'PICKUP'
  },
  sd: {
    action: 'SANDDOLLAR_LOAD' | 'WITHDRAWAL_SANDDOLLAR' | 'SANDDOLLAR_SEND',
    deposit: number,
    fees: { fee: number, vat: number, stamp_tax: number },
    wallet: MobileWallet,
    card_details: string,
    payment_type: 'CUSTOM_NAME' | 'CUSTOM_ADDRESS',
    withdraw_amount: string,
  },
  bd: {
    bank: Bank,
    bankId: string,
    branchId: string,
    newBankAccount: NewAccount
  }
  gh: {
    gaming_house: ExtendedGamingHouse,
    account: {
      auth_type: 'manual' | 'qr' | 'magstripe',
      identifier: string,
      pin: string,
      info: z.infer<typeof GamingHousePlayerSchema>
    }
  },
  TU: {
    mobile_number: string,
    isAliv: boolean,
    isBTC: boolean,
    provider: string,
    logo: string,
    otherCountry: z.infer<typeof Country>,
    addons: any,
    addOnsSelected: any,
    topUpPlan: z.infer<typeof PrepaidPlan>,
  },
  TUOtherCountry: {
    operator: Carrier,
    otherCountry: z.infer<typeof Country>
  },
  BTC: {
    product: BTCProduct,
    product_plan: BTCProductPlan
  },
  ui: {
    service: keyof typeof SERVICE_FEES,
    mobile_number: string,
    deposit: {
      cash: number,
      amount: number,
      invoice_number: string,
      confirm_items: ConfirmItem[],
      fees: {
        fee: number,
        vat: number
      },
      emv: EMVResponse,
      logo: string,
      min: number,
      max: number,
    },
    withdraw: {
      amount: number,
      balance: number,
      has_balance: boolean,
      confirm_items: ConfirmItem[],
      fees: {
        fee: number,
        vat: number
      },
      invoice_number: string,
      logo: string,
      min: number,
      max: number,
    }
    referrer: StepKey,
    option: { key: string, value: any },
  },
  "m-add_cash": {
    cassettes: Cassette[],
    bin: Cassette,
    meters: { [key: string | number]: { count: number, value: number, total: number } },
    [key: `billcount_${number}`]: number,
  },
  "m-full_replenish": boolean,
  "m-replenish_acceptor": any,
  admin: ZAdmin,
  countries: z.infer<typeof Country>[],
  wu: {
    customer: CUSTOMER_INFO,
    mtcn: string,
    estimate: {
      amount: number,
      price: ESTIMATE_FEE,
      fees: FEE_INQUIRY,
      country: z.infer<typeof Country>,
      confirm_items: ConfirmItem[]
    },
    send: {
      purpose: PURPOSE,
      source: SOURCE,
      fees: FEE_INQUIRY,
      price: ESTIMATE_FEE
    },
    receive: {
      purpose: PURPOSE,
      source: SOURCE,
      verifyAnswer: string
    },
    receiver: RECEIVER,
    tx: TRANSACTION_INQUIRY,
  },
  receipt_data: (
    { receipt_type: 'wu_send' } & {
      [K in keyof WUSend]: WUSend[K]
    }
  )
  | {},
  lastDispenserReset: number
}

type PropertyAccessor<ObjectType, Key extends keyof ObjectType, Current extends number, Limit = 0> = Key extends string
  ? ObjectType[Key] extends Record<string, unknown>
  ? Limit extends Current ?
  Key :
  `${Key}.${PropertyAccessor<ObjectType[Key], keyof ObjectType[Key], Increment<Current>, Limit>}`
  : Key
  : never;

export type Increment<N extends number> = [
  1, 2, 3, 4,
  //  5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
  // 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37,
  // 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, // as far as you need
  // ...number[] // bail out with number
][N]

type NestedObjectType<T, Current extends number, Limit = 0> = {
  [K in PropertyAccessor<T, keyof T, Current, Limit>]: K extends `${infer Head}.${infer Rest}`
  ? Head extends keyof T
  ? Rest extends PropertyAccessor<T[Head], keyof T[Head], Increment<Current>, Limit>
  ? NestedObjectType<T[Head], Increment<Current>, Limit>[Rest]
  : never
  : never
  : K extends keyof T
  ? T[K]
  : never;
};


type Params<P, L extends number, I extends number = 0> = I extends L ? NestedObjectType<P, 0, I> : NestedObjectType<P, 0, I> & Params<P, L, Increment<I>>
export type Schema = Params<BaseSchema, 3>;

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private namespace: string = 'sk';
  private listeners: { e: keyof Schema | '*', cb: (value: any, key: string) => void }[] = [];

  private handleError<T>(callback: () => T): T | null {
    try {
      return callback();
    } catch (error) {
      console.error("StorageService Error:", error);
      return null;
    }
  }

  private getData(): Record<string, any> {
    return this.handleError(() => JSON.parse(localStorage.getItem(this.namespace) || '{}'));
  }

  private setData(value: any) {
    this.handleError(() => localStorage.setItem(this.namespace, JSON.stringify(value)));
  }

  private traverseKeys(storage: any, keys: string[]): any {
    let current = storage;
    for (const key of keys) {
      if (current[key] === undefined) {
        return null; // Key path does not exist
      }
      current = current[key];
    }
    return current;
  }

  get<T extends keyof Schema>(key: T, optional?: Schema[T]): Schema[T] {
    const storage = this.getData();
    const keys = key.indexOf(':') >= 0 ? key.split(':') : key.split('.');
    const result = this.traverseKeys(storage, keys);
    return result !== null ? result as Schema[T] : optional as Schema[T];
  }

  set<T extends keyof Schema>(key: T, value: Schema[T]) {
    const storage = this.getData();
    const keys = key.indexOf(':') >= 0 ? key.split(':') : key.split('.');
    let target = storage;

    keys.forEach((key, i) => {
      if (i === keys.length - 1) {
        target[key] = value;
      } else {
        target[key] = target[key] || {};
        target = target[key];
      }
    });

    this.setData(storage);
    this.notifyListeners(key, value);
  }

  private notifyListeners<T extends keyof Schema>(key: T, value: Schema[T]) {
    this.listeners.forEach((listener) => {
      if (listener.e === key || listener.e === '*') {
        listener.cb(value, key);
      }
    });
  }

  remove<T extends keyof Schema>(key: T) {
    const storage = this.getData();
    const keys = key.indexOf(':') >= 0 ? key.split(':') : key.split('.');
    const parent = this.traverseKeys(storage, keys.slice(0, keys.length - 1));

    if (parent) {
      delete parent[keys[keys.length - 1]];
      this.setData(storage);
    }
  }

  clear() {
    localStorage.removeItem(this.namespace);
  }

  onSet<T extends keyof Schema>(e: T | '*', cb: (value: Schema[T], key: string) => void) {
    if (!this.listeners.some(listener => listener.e === e && listener.cb === cb)) {
      this.listeners.push({ e, cb });
    }
  }

  off<T extends keyof Schema>(e: T | '*', cb?: (value: Schema[T], key: string) => void) {
    this.listeners = this.listeners.filter((listener) => {
      return cb ? !(listener.e === e && listener.cb === cb) : listener.e !== e;
    });
  }
}
